import { useEffect, useState, useMemo, useCallback } from 'react';
import { formatCurrency, formatLocal } from '../../lib/currency';
import { useDatabase } from '../DatabaseProvider';
import type {
  CategoryDocType,
  PaymentMethodDocType,
  BranchDocType,
  ExchangeRateDocType,
  AppConfigDocType,
  ServiceDocType,
  UserProfileDocType,
  CustomerDocType,
  StaffDocType,
  ServiceCategoryDocType,
} from '../schemas';
import { generateId, now } from '../../lib/uuid';
import { useAuthStore } from '../../stores';
import { getRateForDate } from '../../lib/rates';
import { dayjs } from '../../lib/dayjs';

export function useCategories(selectedBranchId?: string | null) {
  const db = useDatabase();
  const [categories, setCategories] = useState<CategoryDocType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const query = db.categories.find({
      selector: { is_deleted: false },
    });
    const sub = query.$.subscribe((docs) => {
      setCategories(docs.map((d) => d.toJSON()) as CategoryDocType[]);
      setIsLoading(false);
    });
    return () => sub.unsubscribe();
  }, [db]);

  const activeCategories = useMemo(
    () => categories.filter(
      (c) => c.is_active !== false && (!c.branch_id || c.branch_id === selectedBranchId)
    ),
    [categories, selectedBranchId]
  );

  return { categories, activeCategories, isLoading };
}

export function usePaymentMethods(selectedBranchId?: string | null) {
  const db = useDatabase();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodDocType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const query = db.payment_methods.find({
      selector: { is_deleted: false },
    });
    const sub = query.$.subscribe((docs) => {
      setPaymentMethods(docs.map((d) => d.toJSON()) as PaymentMethodDocType[]);
      setIsLoading(false);
    });
    return () => sub.unsubscribe();
  }, [db]);

  const activePaymentMethods = useMemo(
    () => paymentMethods.filter(
      (m) => m.is_active !== false && (!m.branch_id || m.branch_id === selectedBranchId)
    ),
    [paymentMethods, selectedBranchId]
  );

  return { paymentMethods, activePaymentMethods, isLoading };
}

export function useBranches() {
  const db = useDatabase();
  const [branches, setBranches] = useState<BranchDocType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const query = db.branches.find({
      selector: { is_deleted: false },
    });
    const sub = query.$.subscribe((docs) => {
      setBranches(docs.map((d) => d.toJSON()));
      setIsLoading(false);
    });
    return () => sub.unsubscribe();
  }, [db]);

  const activeBranches = useMemo(
    () => branches.filter((s) => s.is_active !== false),
    [branches]
  );

  return { branches, activeBranches, isLoading };
}

export function useExchangeRatesList() {
  const db = useDatabase();
  const [rates, setRates] = useState<ExchangeRateDocType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const query = db.exchange_rates.find({
      selector: { is_deleted: false },
    });
    const sub = query.$.subscribe((docs) => {
      const sorted = docs.map((d) => d.toJSON());
      sorted.sort((a, b) => b.since.localeCompare(a.since));
      setRates(sorted);
      setIsLoading(false);
    });
    return () => sub.unsubscribe();
  }, [db]);

  return { rates, isLoading };
}

/**
  * Custom Hook: Returns the active exchange rate for a given target date or today's local date.
  * Memoizes results using useMemo to prevent unnecessary re-renders.
  */
export function useActiveRate(targetDateStr?: string) {
  const { rates, isLoading } = useExchangeRatesList();

  const activeRate = useMemo(() => {
    if (!rates || rates.length === 0) return null;
    const targetDate = targetDateStr || dayjs().format('YYYY-MM-DD');
    return getRateForDate(targetDate, rates);
  }, [rates, targetDateStr]);

  const isRateExpired = useMemo(() => {
    if (!activeRate) return true;
    const today = targetDateStr || dayjs().format('YYYY-MM-DD');
    return activeRate.until < today;
  }, [activeRate, targetDateStr]);

  return { activeRate, isRateExpired, rates, isLoading };
}

export function useAppConfigList() {
  const db = useDatabase();
  const [config, setConfig] = useState<AppConfigDocType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const query = db.app_config.find({
      selector: { is_deleted: false },
    });
    const sub = query.$.subscribe((docs) => {
      setConfig(docs.map((d) => d.toJSON()));
      setIsLoading(false);
    });
    return () => sub.unsubscribe();
  }, [db]);

  return { config, isLoading };
}

export function useBaseCurrency() {
  const { config, isLoading } = useAppConfigList();
  const baseCurrencyDoc = config.find((c) => c.key === 'base_currency');
  const baseCurrency = (baseCurrencyDoc?.value as 'USD' | 'EUR' | 'VES') || 'USD';
  const currencySymbol = baseCurrency === 'USD' ? '$' : baseCurrency === 'EUR' ? '€' : 'Bs.S';

  const format = useCallback(
    (amount: number | null | undefined) => formatCurrency(amount, baseCurrency),
    [baseCurrency]
  );

  const formatLocalValue = useCallback(
    (amount: number | null | undefined) => formatLocal(amount),
    []
  );

  return { baseCurrency, currencySymbol, format, formatLocal: formatLocalValue, isLoading };
}

export function useServices(selectedBranchId?: string | null) {
  const db = useDatabase();
  const [services, setServices] = useState<ServiceDocType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const query = db.services.find({
      selector: { is_deleted: false },
    });
    const sub = query.$.subscribe((docs) => {
      setServices(docs.map((d) => d.toJSON()) as ServiceDocType[]);
      setIsLoading(false);
    });
    return () => sub.unsubscribe();
  }, [db]);

  const activeServices = useMemo(
    () => services.filter(
      (s) => !s.branch_id || s.branch_id === selectedBranchId
    ),
    [services, selectedBranchId]
  );

  return { services, activeServices, isLoading };
}

export function usePackages() {
  const db = useDatabase();
  const [packages, setPackages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const query = db.packages.find({
      selector: { is_deleted: false },
    });
    const sub = query.$.subscribe((docs) => {
      setPackages(docs.map((d) => d.toJSON()));
      setIsLoading(false);
    });
    return () => sub.unsubscribe();
  }, [db]);

  const activePackages = useMemo(
    () => packages.filter((p) => p.is_active !== false),
    [packages]
  );

  return { packages, activePackages, isLoading };
}

export function useServiceCategories() {
  const db = useDatabase();
  const [serviceCategories, setServiceCategories] = useState<ServiceCategoryDocType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const query = db.service_categories.find({
      selector: { is_deleted: false },
      sort: [{ sort_order: 'asc' }],
    });
    const sub = query.$.subscribe((docs) => {
      setServiceCategories(docs.map((d) => d.toJSON()) as ServiceCategoryDocType[]);
      setIsLoading(false);
    });
    return () => sub.unsubscribe();
  }, [db]);

  return { serviceCategories, isLoading };
}

export function useUserProfiles() {
  const db = useDatabase();
  const [profiles, setProfiles] = useState<UserProfileDocType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const query = db.user_profiles.find({
      selector: { is_deleted: false },
    });
    const sub = query.$.subscribe((docs) => {
      setProfiles(docs.map((d) => d.toJSON()) as UserProfileDocType[]);
      setIsLoading(false);
    });
    return () => sub.unsubscribe();
  }, [db]);

  return { profiles, isLoading };
}

export function useUserProfileMap() {
  const { profiles, isLoading } = useUserProfiles();

  const profilesMap = useMemo(() => {
    const map = new Map<string, UserProfileDocType>();
    for (const p of profiles) {
      map.set(p.id, p);
    }
    return map;
  }, [profiles]);

  const getUserLabel = useCallback(
    (userId?: string | null) => {
      if (!userId || userId === 'system') return 'Sistema';
      const p = profilesMap.get(userId);
      if (p) {
        return p.name ? `${p.name} (${p.job_title || (p.is_owner ? 'Global Admin' : p.role || 'Staff')})` : p.email;
      }
      return userId;
    },
    [profilesMap]
  );

  return { profilesMap, getUserLabel, isLoading };
}

export function useCustomers(branchId?: string | null) {
  const db = useDatabase();
  const [customers, setCustomers] = useState<CustomerDocType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const selector: any = { is_deleted: false };
    if (branchId && branchId !== 'all') {
      selector.branch_id = branchId;
    }
    const query = db.customers.find({ selector });
    const sub = query.$.subscribe((docs) => {
      setCustomers(docs.map((d) => d.toJSON()) as CustomerDocType[]);
      setIsLoading(false);
    });
    return () => sub.unsubscribe();
  }, [db, branchId]);

  return { customers, isLoading };
}

export function useStaff(branchId?: string | null) {
  const db = useDatabase();
  const [staff, setStaff] = useState<StaffDocType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const selector: any = { is_deleted: false };
    if (branchId) {
      selector.default_branch_id = branchId;
    }
    const query = db.staff.find({ selector });
    const sub = query.$.subscribe((docs) => {
      setStaff(docs.map((d) => d.toJSON()) as StaffDocType[]);
      setIsLoading(false);
    });
    return () => sub.unsubscribe();
  }, [db, branchId]);

  return { staff, isLoading };
}

/**
 * Mutation helper to create lookup data.
 */
export function useLookupMutations() {
  const db = useDatabase();
  const profile = useAuthStore((s) => s.profile);
  const user = useAuthStore((s) => s.user);
  const currentUserId = profile?.id || user?.id || 'system';

  const addCategory = async (
    param: string | { name: string; type: 'income' | 'expense' | 'both'; color?: string; icon?: string },
    defaultType: 'income' | 'expense' | 'both' = 'income',
    defaultColor = '#94A3B8',
    defaultIcon = '📁'
  ) => {
    let name = '';
    let type = defaultType;
    let color = defaultColor;
    let icon = defaultIcon;

    if (typeof param === 'object' && param !== null) {
      name = param.name || '';
      type = param.type || 'income';
      color = param.color || '#94A3B8';
      icon = param.icon || '📁';
    } else {
      name = param || '';
    }

    const id = generateId();
    const timestamp = now();
    const docData = {
      id,
      name,
      type,
      color,
      icon,
      is_active: true,
      created_at: timestamp,
      updated_at: timestamp,
      is_deleted: false,
      last_modified_by: currentUserId,
    };
    const doc = await db.categories.insert(docData);
    return doc.toJSON();
  };

  const updateCategory = async (id: string, updates: Partial<CategoryDocType>) => {
    const existing = await db.categories.findOne(id).exec();
    if (existing) {
      await existing.patch({
        ...updates,
        updated_at: now(),
        last_modified_by: currentUserId,
      });
    }
  };

  const deleteCategory = async (id: string) => {
    const existing = await db.categories.findOne(id).exec();
    if (existing) {
      await existing.patch({
        is_deleted: true,
        updated_at: now(),
        last_modified_by: currentUserId,
      });
    }
  };

  const addPaymentMethod = async (
    param: string | { name: string; type: 'local' | 'foreign'; currency?: 'VES' | 'USD' | 'EUR' },
    defaultType: 'local' | 'foreign' = 'local',
    defaultCurrency: 'VES' | 'USD' | 'EUR' = 'VES'
  ) => {
    let name = '';
    let type = defaultType;
    let currency = defaultCurrency;

    if (typeof param === 'object' && param !== null) {
      name = param.name || '';
      type = param.type || 'local';
      currency = param.currency || 'VES';
    } else {
      name = param || '';
    }

    const id = generateId();
    const timestamp = now();
    const docData = {
      id,
      name,
      type,
      currency,
      is_active: true,
      created_at: timestamp,
      updated_at: timestamp,
      is_deleted: false,
      last_modified_by: currentUserId,
    };
    const doc = await db.payment_methods.insert(docData);
    return doc.toJSON();
  };

  const addExchangeRate = async (
    param1: number | { eur: number; usd: number; since?: string; until?: string; overrideId?: string; lastModifiedBy?: string },
    pUsdOrEur?: number,
    pSince?: string,
    pUntil?: string,
    pOverrideIdOrLastModifiedBy?: string,
    pLastModifiedBy?: string
  ) => {
    const today = now();
    let eur = 0;
    let usd = 0;
    let sinceStr = today.substring(0, 10);
    let untilStr = sinceStr;
    let overrideId: string | undefined = undefined;
    let modifier = currentUserId;

    if (typeof param1 === 'object' && param1 !== null) {
      eur = param1.eur || 0;
      usd = param1.usd || 0;
      sinceStr = (param1.since || today).substring(0, 10);
      untilStr = (param1.until || sinceStr).substring(0, 10);
      overrideId = param1.overrideId;
      modifier = param1.lastModifiedBy || currentUserId;
    } else {
      eur = param1 || 0;
      usd = pUsdOrEur || 0;
      sinceStr = (pSince || today).substring(0, 10);
      untilStr = (pUntil || sinceStr).substring(0, 10);
      overrideId = pOverrideIdOrLastModifiedBy && !pLastModifiedBy ? undefined : pOverrideIdOrLastModifiedBy;
      modifier = pLastModifiedBy || pOverrideIdOrLastModifiedBy || currentUserId;
    }

    const id = overrideId || generateId();
    const existing = await db.exchange_rates.findOne(id).exec();
    if (!existing) {
      const doc = await db.exchange_rates.insert({
        id,
        since: sinceStr,
        until: untilStr,
        eur,
        usd,
        created_at: today,
        updated_at: today,
        is_deleted: false,
        last_modified_by: modifier,
      });
      return doc.toJSON();
    } else if (existing.is_deleted) {
      await existing.patch({
        is_deleted: false,
        since: sinceStr,
        until: untilStr,
        eur,
        usd,
        updated_at: today,
        last_modified_by: modifier,
      });
      return existing.toJSON();
    }
  };

  const removeFallbackRate = async () => {
    try {
      const fallbackRateDoc = await db.exchange_rates.findOne({ selector: { id: 'default-fallback-rate' } }).exec();
      if (fallbackRateDoc) {
        await fallbackRateDoc.remove();
        console.log('[Seed/Sync] Fallback rate discarded.');
      }
    } catch (err) {
      console.warn('Error removing fallback rate:', err);
    }
  };

  const addCustomer = async (
    param: string | { name: string; branch_id: string; phone?: string | null; email?: string | null; instagram?: string | null; notes?: string | null; createdAt?: string | null },
    pBranchId?: string,
    pPhone?: string | null,
    pEmail?: string | null,
    pInstagram?: string | null,
    pNotes?: string | null,
    pCreatedAt?: string | null
  ) => {
    let name = '';
    let branch_id = pBranchId || '';
    let phone = pPhone || null;
    let email = pEmail || null;
    let instagram = pInstagram || null;
    let notes = pNotes || null;
    let createdAt = pCreatedAt || null;

    if (typeof param === 'object' && param !== null) {
      name = param.name || '';
      branch_id = param.branch_id || pBranchId || '';
      phone = param.phone || null;
      email = param.email || null;
      instagram = param.instagram || null;
      notes = param.notes || null;
      createdAt = param.createdAt || null;
    } else {
      name = param || '';
    }

    const id = generateId();
    const timestamp = now();
    const doc = await db.customers.insert({
      id,
      name,
      branch_id,
      phone: phone || null,
      email: email || null,
      instagram: instagram || null,
      notes: notes || null,
      credit_balance: (param as any)?.credit_balance ?? 0,
      debt_balance: (param as any)?.debt_balance ?? 0,
      created_at: createdAt || timestamp,
      updated_at: timestamp,
      is_deleted: false,
      last_modified_by: currentUserId,
    });
    return doc.toJSON();
  };

  const addStaff = async (
    param: string | { name: string; default_branch_id?: string; role?: string | null; phone?: string | null; email?: string | null; monthly_salary?: number | null; pay_frequency?: string; work_days?: any; createdAt?: string | null },
    pDefaultBranchId?: string,
    pRole?: string | null,
    pPhone?: string | null,
    pEmail?: string | null,
    pMonthlySalary?: number | null,
    pPayFrequency: string = 'biweekly',
    pWorkDays?: Record<string, any> | null,
    pCreatedAt?: string | null
  ) => {
    let name = '';
    let default_branch_id = pDefaultBranchId || '';
    let role = pRole || null;
    let phone = pPhone || null;
    let email = pEmail || null;
    let monthly_salary = pMonthlySalary ?? null;
    let pay_frequency: string = pPayFrequency;
    let work_days = pWorkDays || null;
    let createdAt = pCreatedAt || null;

    if (typeof param === 'object' && param !== null) {
      name = param.name || '';
      default_branch_id = param.default_branch_id || pDefaultBranchId || '';
      role = param.role || null;
      phone = param.phone || null;
      email = param.email || null;
      monthly_salary = param.monthly_salary ?? null;
      pay_frequency = param.pay_frequency || 'biweekly';
      work_days = param.work_days || null;
      createdAt = param.createdAt || null;
    } else {
      name = param || '';
    }

    const id = generateId();
    const timestamp = now();
    const doc = await db.staff.insert({
      id,
      name,
      default_branch_id,
      is_active: true,
      phone: phone || null,
      email: email || null,
      role: role || null,
      daily_salary: 0,
      work_days: work_days || {
        monday: 'active',
        tuesday: 'active',
        wednesday: 'active',
        thursday: 'active',
        friday: 'active',
        saturday: 'active',
        sunday: 'off',
      },
      pay_frequency,
      monthly_salary,
      created_at: createdAt || timestamp,
      updated_at: timestamp,
      is_deleted: false,
      last_modified_by: currentUserId,
    });
    return doc.toJSON();
  };

  const addServiceCategory = async (
    param: string | { name: string; color?: string; sort_order?: number },
    paramColor = '#94A3B8',
    paramSortOrder = 0
  ) => {
    let name = '';
    let color = paramColor;
    let sort_order = paramSortOrder;

    if (typeof param === 'object' && param !== null) {
      name = param.name || '';
      color = param.color || paramColor;
      sort_order = param.sort_order ?? paramSortOrder;
    } else {
      name = param || '';
    }

    const id = generateId();
    const timestamp = now();
    const doc = await db.service_categories.insert({
      id,
      name,
      color,
      sort_order,
      created_at: timestamp,
      updated_at: timestamp,
      is_deleted: false,
      last_modified_by: currentUserId,
    });
    return doc.toJSON();
  };

  const updateServiceCategory = async (id: string, updates: Partial<ServiceCategoryDocType>) => {
    const existing = await db.service_categories.findOne(id).exec();
    if (existing) {
      await existing.patch({
        ...updates,
        updated_at: now(),
        last_modified_by: currentUserId,
      });
    }
  };

  const deleteServiceCategory = async (id: string) => {
    const existing = await db.service_categories.findOne(id).exec();
    if (existing) {
      await existing.patch({
        is_deleted: true,
        updated_at: now(),
        last_modified_by: currentUserId,
      });
    }
  };

  return {
    addCustomer,
    addStaff,
    addCategory,
    updateCategory,
    deleteCategory,
    addPaymentMethod,
    addExchangeRate,
    removeFallbackRate,
    addServiceCategory,
    updateServiceCategory,
    deleteServiceCategory,
  };
}

export function useStaffRoles() {
  const db = useDatabase();
  const user = useAuthStore((s) => s.user);
  const [roles, setRoles] = useState<string[]>(['Administrador', 'Recepcionista', 'Operador']);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const query = db.app_config.findOne({
      selector: { key: 'staff_roles', is_deleted: false },
    });
    const sub = query.$.subscribe((doc) => {
      if (doc) {
        try {
          const parsed = JSON.parse(doc.value);
          if (Array.isArray(parsed)) {
            setRoles(parsed);
          }
        } catch (e) {
          console.error('Error parsing staff_roles config', e);
        }
      }
      setIsLoading(false);
    });
    return () => sub.unsubscribe();
  }, [db]);

  const addRole = async (newRole: string) => {
    const trimmed = newRole.trim();
    if (!trimmed) return;
    const normalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    if (roles.includes(normalized)) return;

    const newRoles = [...roles, normalized];
    setRoles(newRoles);

    const existingDoc = await db.app_config.findOne({ selector: { key: 'staff_roles' } }).exec();
    const timestamp = now();
    const jsonVal = JSON.stringify(newRoles);

    if (existingDoc) {
      await existingDoc.patch({
        value: jsonVal,
        updated_at: timestamp,
        is_deleted: false,
        last_modified_by: user?.email || 'system_role',
      });
    } else {
      await db.app_config.insert({
        id: generateId(),
        key: 'staff_roles',
        value: jsonVal,
        updated_at: timestamp,
        is_deleted: false,
        last_modified_by: user?.email || 'system_role',
      });
    }
  };

  const removeRole = async (roleToRemove: string) => {
    const newRoles = roles.filter((r) => r !== roleToRemove);
    setRoles(newRoles);

    const existingDoc = await db.app_config.findOne({ selector: { key: 'staff_roles' } }).exec();
    const timestamp = now();
    const jsonVal = JSON.stringify(newRoles);

    if (existingDoc) {
      await existingDoc.patch({
        value: jsonVal,
        updated_at: timestamp,
        is_deleted: false,
        last_modified_by: user?.email || 'system_role',
      });
    }
  };

  return { roles, isLoading, addRole, removeRole };
}
